/**
 * Pagination controls for a list screen.
 *
 * Reads the Section 5.9 `pagination` block the API already sends, so a screen
 * never computes a page count of its own — the server's total is the only one
 * that has seen the whole table.
 *
 * Renders nothing when there is a single page. A "Page 1 of 1" with two dead
 * buttons is furniture, not information.
 *
 * Sits ABOVE the table and ranges right (Parinaam, 9 Oct 2026). Below the table
 * it was only reachable by scrolling past every row — on the attendance screen,
 * fifty of them — so the control that moves you off a page was the last thing
 * you could reach on it.
 */

import type { Pagination } from '@parinaam-vms/shared';
import { Button } from '../common/Button';
import styles from './Pager.module.css';

export interface PagerProps {
  pagination: Pagination | undefined;
  onPageChange: (page: number) => void;
}

export function Pager({ pagination, onPageChange }: PagerProps) {
  if (!pagination || pagination.total_pages <= 1) return null;

  const { page, page_size, total, total_pages } = pagination;
  const first = (page - 1) * page_size + 1;
  const last = Math.min(page * page_size, total);

  return (
    <nav className={styles.pager} aria-label="Pagination">
      <p className={styles.summary}>
        {first}–{last} of {total}
      </p>

      <div className={styles.controls}>
        {/*
          The bracket alone, with the word moved to `aria-label` (Parinaam,
          9 Oct 2026).

          That move is what keeps the button usable rather than a nicety: a
          button whose only content is a punctuation mark has no accessible
          name, and a screen reader announces it as "button". The label says
          "Previous page" rather than "Previous" because there is no longer a
          word beside it to say what is previous.
        */}
        <Button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          &lsaquo;
        </Button>
        {/* aria-live so a screen reader announces the move; the table itself
            gives no audible sign that its contents were replaced. */}
        <span className={styles.position} aria-live="polite">
          Page {page} of {total_pages}
        </span>
        <Button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= total_pages}
          aria-label="Next page"
        >
          &rsaquo;
        </Button>
      </div>
    </nav>
  );
}
